import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import ItemTabBar from "./itemTabBar";
import AdjustStockModal from "../../components/CustomComponents/modal/AdjustStockModal";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import {
  formatCompanyDateTimeForApi,
  formatLedgerRowDateTime,
  getRowDateTimestamp,
} from "../../utils/companyDateTime";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";

const toInt = (v) => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

const SALES_SUMMARY_OUT_TYPES = new Set([
  "sale",
  "sale_negative",
  "recipeingredient",
  "recipeingredient_negative",
  "recipeproduction",
  "recipeproduction_negative",
  "batchproduction",
  "batchproduction_negative",
]);

const isSalesSummaryRow = (transactionType, quantityChange) => {
  const type = String(transactionType ?? "").toLowerCase();
  if (!SALES_SUMMARY_OUT_TYPES.has(type)) return false;
  if (type.endsWith("_negativefill")) return false;
  return Number.isFinite(quantityChange) && quantityChange < 0;
};

const UpdateItemInventoryLedger = () => {
  const { itemId } = useParams();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { getCompCurrency, getCompTimeZone, getCompUTCTimeZone } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const compTimeZone = getCompTimeZone();
  const compUTCTimeZone = getCompUTCTimeZone();
  const ledgerTzOptions = useMemo(
    () => ({ compTimeZone, compUTCTimeZone }),
    [compTimeZone, compUTCTimeZone]
  );
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code;
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const formatQuantity = (val) => {
    if (val == null || (typeof val !== "number" && isNaN(parseFloat(val)))) return "-";
    const n = parseFloat(val);
    return Number.isInteger(n) ? n.toString() : n.toFixed(2);
  };

  const formatTransactionType = (value) => {
    const raw = String(value || "");
    const normalized = raw.toLowerCase();
    if (normalized === "sale_negative") return "Sale (Negative Stock)";
    if (normalized.endsWith("_negativefill")) {
      const base = raw.slice(0, -"_NegativeFill".length);
      return `${base} (Used to Fill Negative Stock)`;
    }
    return raw || "-";
  };

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [itemData, setItemData] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [dateRange, setDateRange] = useState(() => [
    moment().startOf("month").startOf("day").toDate(),
    moment().endOf("month").endOf("day").toDate(),
  ]);
  const [selectedVariantFilter, setSelectedVariantFilter] = useState({ value: "all", label: "All variants" });
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchFilter, setBranchFilter] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const effectiveBranchFilter = useMemo(() => {
    if (!isMain || !CompID) return null;
    const compIdNum = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
    const defaultOption = { value: compIdNum, label: profileData?.compName ?? profileData?.CompName ?? "This branch only" };
    return branchFilter ?? defaultOption;
  }, [isMain, CompID, branchFilter, profileData?.compName, profileData?.CompName]);

  const hasVariants = useMemo(() => {
    const hv = itemData?.menuItem_HasVariant ?? itemData?.MenuItem_HasVariant ?? false;
    const products = Array.isArray(itemData?.products) ? itemData.products : [];
    return !!hv || products.length > 0;
  }, [itemData]);

  // Filter ledger to selected branch only (safety net + ensures stock adjustments respect branch)
  const ledgerDataForBranch = useMemo(() => {
    const selectedBranchId = effectiveBranchFilter?.value != null && effectiveBranchFilter.value !== 0
      ? (typeof effectiveBranchFilter.value === "number" ? effectiveBranchFilter.value : parseInt(effectiveBranchFilter.value, 10))
      : null;
    if (selectedBranchId == null || !Number.isFinite(selectedBranchId)) return ledgerData;
    return ledgerData.filter((row) => {
      const rowCompId = row.compID ?? row.CompID;
      const num = rowCompId != null ? (typeof rowCompId === "number" ? rowCompId : parseInt(rowCompId, 10)) : null;
      return num === selectedBranchId;
    });
  }, [ledgerData, effectiveBranchFilter]);

  const salesSummary = useMemo(() => {
    const toNumber = (value) => {
      if (value == null || value === "") return 0;
      const parsed = typeof value === "number" ? value : parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const getSaleAmountFromRow = (row, absQty) => {
      const directSale =
        row.totalSale ??
        row.TotalSale ??
        row.saleTotal ??
        row.SaleTotal ??
        row.saleAmount ??
        row.SaleAmount ??
        row.totalAmount ??
        row.TotalAmount ??
        row.amount ??
        row.Amount ??
        row.lineTotal ??
        row.LineTotal;

      if (directSale != null && directSale !== "") {
        const parsedDirectSale = toNumber(directSale);
        if (parsedDirectSale !== 0) return parsedDirectSale;
      }

      const unitSalePrice =
        row.salePrice ??
        row.SalePrice ??
        row.menuItem_SalePrice ??
        row.MenuItem_SalePrice ??
        row.unitPrice ??
        row.UnitPrice ??
        row.sellingPrice ??
        row.SellingPrice;

      return absQty * toNumber(unitSalePrice);
    };

    const getCostAmountFromRow = (row, absQty) => {
      const directCost = toNumber(row.totalCost ?? row.TotalCost);
      if (directCost !== 0) return directCost;

      const unitCost =
        row.costPrice ??
        row.CostPrice ??
        row.menuItem_CostPrice ??
        row.MenuItem_CostPrice;

      return absQty * toNumber(unitCost);
    };

    const totals = (ledgerDataForBranch || []).reduce(
      (acc, row) => {
        const type = row.transactionType ?? row.TransactionType ?? "";
        const qtyChange = toNumber(row.quantityChange ?? row.QuantityChange);
        if (!isSalesSummaryRow(type, qtyChange)) return acc;

        const absQty = Math.abs(qtyChange);
        acc.saleQty += absQty;
        acc.totalSale += getSaleAmountFromRow(row, absQty);
        acc.totalCost += getCostAmountFromRow(row, absQty);
        return acc;
      },
      { saleQty: 0, totalSale: 0, totalCost: 0 }
    );

    return totals;
  }, [ledgerDataForBranch]);

  const variantOptions = useMemo(() => {
    const opts = [{ value: "all", label: "All variants" }];
    const list = Array.isArray(itemData?.products) ? itemData.products : [];
    list.forEach((v) => {
      const vid = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
      const vname = v?.menuItem_Name ?? v?.MenuItem_Name ?? v?.menuItem_DefaultVariantTitle ?? v?.MenuItem_DefaultVariantTitle ?? `Variant #${vid}`;
      if (vid != null && vid > 0) opts.push({ value: vid, label: vname });
    });
    return opts;
  }, [itemData]);

  const fetchItemData = useCallback(async () => {
    if (!itemId || !CompID) return;
    try {
      const res = await axiosInstance.get(APIs.GET_ITEM_BY_ID, { params: { MenuItem_ID: itemId, CompID } });
      const data = res?.data?.data ?? res?.data;
      setItemData(data);
    } catch (e) {
      showErrorToast("Failed to load item");
    }
  }, [itemId, CompID]);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "This branch only";
      const base = [
        { value: "all", label: "All branches" },
        { value: parentId, label: compName },
      ];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (pidNum === parentId && idNum !== parentId) return true;
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      setBranchOptions([...base, ...branchOpts]);
    } catch (e) {
      showErrorToast("Failed to load branches");
      setBranchOptions([
        { value: "all", label: "All branches" },
        { value: typeof CompID === "number" ? CompID : parseInt(CompID, 10), label: profileData?.compName ?? profileData?.CompName ?? "This branch only" },
      ]);
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  const fetchLedgerData = useCallback(async () => {
    if (!itemId || !CompID) return;
    setLoading(true);
    try {
      const [startDate, endDate] = dateRange;
      // Always pass branch: for main company use selected branch; for branch users use their CompID so only their branch is returned
      const selectedLocationValue = effectiveBranchFilter?.value;
      const selectedLocationNum =
        typeof selectedLocationValue === "number"
          ? selectedLocationValue
          : parseInt(selectedLocationValue, 10);
      const locationFilterValue = isMain
        ? (Number.isFinite(selectedLocationNum) && selectedLocationNum > 0 ? selectedLocationNum : null)
        : (CompID != null && CompID !== 0 ? CompID : null);
      const baseParams = {
        CompID,
        StartDate: formatCompanyDateTimeForApi(startDate, ledgerTzOptions),
        EndDate: formatCompanyDateTimeForApi(endDate, ledgerTzOptions),
        PageNumber: 1,
        PageSize: 200,
        SortBy: "TransactionDate",
        SortOrder: "DESC",
        ...(locationFilterValue != null && { LocationFilter: locationFilterValue }),
      };

      const selectedValue = selectedVariantFilter?.value;
      const isAll = selectedValue === "all" && hasVariants;

      if (isAll) {
        const ids = [parseInt(itemId, 10), ...(variantOptions || []).filter((o) => o.value !== "all").map((o) => o.value)];
        const uniqueIds = [...new Set(ids.filter((id) => id != null && !isNaN(id)))];
        const promises = uniqueIds.map((menuItemId) =>
          axiosInstance.get(APIs.GET_INVENTORY_LEDGER_REPORT, {
            params: { ...baseParams, MenuItemFilter: menuItemId },
          })
        );
        const results = await Promise.allSettled(promises);
        const merged = [];
        results.forEach((r) => {
          if (r.status === "fulfilled" && r.value?.data?.data) {
            const arr = Array.isArray(r.value.data.data) ? r.value.data.data : [];
            merged.push(...arr);
          }
        });
        const byId = {};
        merged.forEach((row) => {
          const id = row.ledgerId ?? row.LedgerId;
          if (id != null && !byId[id]) byId[id] = row;
        });
        const sorted = Object.values(byId).sort((a, b) => getRowDateTimestamp(b) - getRowDateTimestamp(a));
        setLedgerData(sorted);
      } else {
        const menuItemId = selectedValue === "all" ? parseInt(itemId, 10) : (typeof selectedValue === "number" ? selectedValue : parseInt(selectedValue, 10));
        const res = await axiosInstance.get(APIs.GET_INVENTORY_LEDGER_REPORT, {
          params: { ...baseParams, MenuItemFilter: menuItemId },
        });
        const data = res?.data?.data ?? res?.data ?? [];
        setLedgerData(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      showErrorToast("Failed to load ledger");
      setLedgerData([]);
    } finally {
      setLoading(false);
    }
  }, [itemId, CompID, dateRange, selectedVariantFilter, hasVariants, variantOptions, isMain, effectiveBranchFilter, ledgerTzOptions]);

  useEffect(() => {
    fetchItemData();
  }, [fetchItemData]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    if (itemId && CompID) {
      setInitialLoading(false);
      fetchLedgerData();
    }
  }, [fetchLedgerData, itemId, CompID]);

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      let effectiveStartDate = newStartDate;
      let effectiveEndDate = newEndDate;
      if (newStartDate && newEndDate && newStartDate > newEndDate) {
        [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
      } else if (newStartDate && !newEndDate) {
        effectiveEndDate = new Date(newStartDate.getTime());
        effectiveEndDate.setHours(23, 59, 59, 999);
      }
      setDateRange([
        effectiveStartDate ?? dateRange[0],
        effectiveEndDate ?? dateRange[1],
      ]);
    } else {
      showErrorToast("Invalid date selected");
    }
  };

  const handleAdjustSuccess = () => {
    fetchLedgerData();
  };

  const itemName = itemData?.menuItem_Name ?? itemData?.MenuItem_Name ?? `Item #${itemId}`;
  const showAllVariants = hasVariants && selectedVariantFilter?.value === "all";
  const isAllBranchesSelected = isMain && String(effectiveBranchFilter?.value ?? "") === "all";
  const adjustStockMenuItemId = showAllVariants ? null : (selectedVariantFilter?.value != null && selectedVariantFilter?.value !== "all" ? selectedVariantFilter.value : parseInt(itemId, 10));
  const canAdjustStock = !hasVariants || (hasVariants && selectedVariantFilter?.value !== "all");
  const adjustStockLabel = itemData && hasVariants && selectedVariantFilter?.value === "all"
    ? (selectedVariantFilter?.label ?? "All variants")
    : (selectedVariantFilter?.label ?? itemName);
  // Use selected branch for adjustments so stock changes apply to the branch being viewed
  const adjustStockCompID = (effectiveBranchFilter?.value != null && effectiveBranchFilter.value !== 0)
    ? (typeof effectiveBranchFilter.value === "number" ? effectiveBranchFilter.value : parseInt(effectiveBranchFilter.value, 10))
    : CompID;

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
          <li className="breadcrumb-item"><Link to="/items/manageItems">Manage Items</Link></li>
          <li className="breadcrumb-item"><Link to={`/items/updateItem/${itemId}`}>Update Item</Link></li>
          <li className="breadcrumb-item active">Inventory Ledger</li>
        </ol>
        <h1 className="page-header mb-0" style={{ fontSize: "1.35rem" }}>Inventory Ledger</h1>
        <p className="text-muted mb-0 mt-1" style={{ fontSize: "13px" }}>{itemName}</p>
      </div>
    </div>
  );

  if (initialLoading && !itemData) {
    return (
      <div className="container-fluid p-3">
        <Preloader />
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}
      {renderBreadcrumb()}
      {itemId && (
        <>
          <ItemTabBar
            itemId={itemId}
            hasUnsavedChanges={false}
            onTabChange={async () => true}
          />
          <div className="row align-items-end g-2 mb-2">
            <div className="col-md-3">
              <label className="form-label mb-0" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500, fontSize: "13px" }}>Date Range</label>
              <DateRangePicker
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={handleDateRangeChange}
                placeholder="Date Range"
                allowSingleDate
                allowDateRange
                showTimeSelection
                className="filter-dropdown"
              />
            </div>
            {isMain && (
              <div className="col-md-3">
                <label className="form-label mb-0" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500, fontSize: "13px" }}>Branch</label>
                <Select
                  value={branchOptions.find((o) => o.value === effectiveBranchFilter?.value) ?? effectiveBranchFilter}
                  onChange={(opt) => setBranchFilter(opt ?? null)}
                  options={branchOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder={loadingLocations ? "Loading..." : "Select branch"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isLoading={loadingLocations}
                />
              </div>
            )}
            {hasVariants && (
              <div className="col-md-3">
                <label className="form-label mb-0" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500, fontSize: "13px" }}>Variant</label>
                <Select
                  value={selectedVariantFilter}
                  onChange={(opt) => setSelectedVariantFilter(opt || { value: "all", label: "All variants" })}
                  options={variantOptions}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder="Select variant"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}
            <div className={`${hasVariants ? "col-md-2" : "col-md-3"} d-flex align-items-end`}>
              <div
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  border: `1px solid ${themeColor}30`,
                  backgroundColor: isDarkMode ? "#1e2124" : "#f8f9fa",
                  padding: "8px 10px",
                  minHeight: "38px",
                }}
              >
                <div style={{ fontSize: "11px", color: isDarkMode ? "#b2bec3" : "#6c757d", fontWeight: 600 }}>
                  Sales Summary
                </div>
                <div
                  className="d-flex justify-content-between"
                  style={{ fontSize: "12px", color: isDarkMode ? "#fff" : "#000", marginTop: 2 }}
                >
                  <span>
                    Qty: {Number(salesSummary.saleQty || 0).toFixed(2)}
                  </span>
                  <span>
                    Sale: {getCurrencySymbol(compCurrency)}
                    {Number(salesSummary.totalSale || 0).toFixed(2)}
                  </span>
                  <span>
                    Cost: {getCurrencySymbol(compCurrency)}
                    {Number(salesSummary.totalCost || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            {!isAllBranchesSelected && (
              <div className={`${hasVariants ? "col-md-2" : "col-md-3"} ms-auto d-flex flex-column align-items-end justify-content-end`}>
                <label className="form-label mb-0 d-block" style={{ fontSize: "13px" }}>&nbsp;</label>
                <button
                  onClick={() => canAdjustStock && setShowAdjustModal(true)}
                  disabled={!canAdjustStock}
                  className="btn btn-primary d-flex align-items-center gap-2"
                  style={{
                    background: canAdjustStock ? `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)` : (isDarkMode ? "#555" : "#ccc"),
                    border: "none",
                    borderRadius: "8px",
                    padding: "6px 14px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: canAdjustStock ? "pointer" : "not-allowed",
                  }}
                  title={!canAdjustStock ? "Select a variant to adjust stock" : undefined}
                >
                  <i className="fa fa-edit"></i>
                  Adjust Stock
                </button>
              </div>
            )}
          </div>

          <Panel className="mt-2">
            <PanelHeader
              style={{
                backgroundColor: themeColor,
                color: "white",
                borderRadius: "10px 10px 0 0",
                padding: "12px 20px",
                fontWeight: "600",
                fontSize: "15px",
              }}
            >
              <i className="fa fa-book me-2"></i>
              Ledger History
            </PanelHeader>
            <PanelBody
              style={{
                padding: "16px 20px",
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                borderRadius: "0 0 10px 10px",
                border: `1px solid ${themeColor}20`,
              }}
            >
              <div className="table-responsive" style={{ maxHeight: "450px", overflowY: "auto" }}>
                <table className="table table-hover" style={{ color: isDarkMode ? "#fff" : "#000", fontSize: "13px" }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: isDarkMode ? "#3c4043" : "#f8f9fa", zIndex: 10 }}>
                    <tr>
                      <th style={{ padding: "8px 12px" }}>Transaction Date & Time</th>
                      {showAllVariants && <th style={{ padding: "8px 12px" }}>Item / Variant</th>}
                      <th style={{ padding: "8px 12px" }}>Type</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Quantity Change</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Qty Before</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Qty After</th>
                      <th style={{ padding: "8px 12px" }}>Unit</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Sale Price</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Cost Price</th>
                      <th className="text-center" style={{ padding: "8px 12px" }}>Total Cost</th>
                      <th style={{ padding: "8px 12px" }}>Reference</th>
                      {isMain && <th style={{ padding: "8px 12px" }}>Branch</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerDataForBranch.length === 0 ? (
                      <tr>
                        <td colSpan={showAllVariants ? (isMain ? 11 : 10) : (isMain ? 10 : 9)} className="text-center py-3 text-muted" style={{ padding: "8px 12px" }}>
                          No ledger entries found
                        </td>
                      </tr>
                    ) : (
                      ledgerDataForBranch.map((row, idx) => {
                        const qty = parseFloat(row.quantityChange ?? row.QuantityChange ?? 0);
                        const isPos = qty >= 0;
                        return (
                          <tr key={row.ledgerId ?? row.LedgerId ?? idx}>
                            <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                              {formatLedgerRowDateTime(row, ledgerTzOptions)}
                            </td>
                            {showAllVariants && (
                              <td style={{ padding: "8px 12px" }}>{row.itemName ?? row.ItemName ?? "-"}</td>
                            )}
                            <td style={{ padding: "8px 12px" }}>
                              {formatTransactionType(row.transactionType ?? row.TransactionType)}
                            </td>
                            <td
                              className="text-center"
                              style={{
                                padding: "8px 12px",
                                color: isPos ? (isDarkMode ? "#55efc4" : "#00b894") : (isDarkMode ? "#ff7675" : "#d63031"),
                                fontWeight: 500,
                              }}
                            >
                              {isPos ? "+" : ""}{formatQuantity(qty)}
                            </td>
                            <td className="text-center" style={{ fontWeight: 500, padding: "8px 12px" }}>
                              {formatQuantity(row.quantityBefore ?? row.QuantityBefore)}
                            </td>
                            <td className="text-center" style={{ fontWeight: 500, padding: "8px 12px" }}>
                              {formatQuantity(row.quantityAfter ?? row.QuantityAfter)}
                            </td>
                            <td style={{ padding: "8px 12px" }}>{row.unit ?? row.Unit ?? "-"}</td>
                            <td className="text-center" style={{ padding: "8px 12px" }}>
                              {row.salePrice != null || row.SalePrice != null
                                ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.salePrice ?? row.SalePrice ?? 0).toFixed(4)}`
                                : "-"}
                            </td>
                            <td className="text-center" style={{ padding: "8px 12px" }}>
                              {row.costPrice != null || row.CostPrice != null
                                ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.costPrice ?? row.CostPrice ?? 0).toFixed(4)}`
                                : "-"}
                            </td>
                            <td className="text-center" style={{ padding: "8px 12px" }}>
                              {row.totalCost != null || row.TotalCost != null
                                ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.totalCost ?? row.TotalCost ?? 0).toFixed(2)}`
                                : "-"}
                            </td>
                            <td style={{ padding: "8px 12px" }}>{row.transactionReference ?? row.TransactionReference ?? "-"}</td>
                            {isMain && <td style={{ padding: "8px 12px" }}>{row.branchName ?? row.BranchName ?? "-"}</td>}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </PanelBody>
          </Panel>

          {adjustStockMenuItemId != null && (
            <AdjustStockModal
              isOpen={showAdjustModal}
              onClose={() => setShowAdjustModal(false)}
              onSuccess={handleAdjustSuccess}
              menuItemId={adjustStockMenuItemId}
              compID={adjustStockCompID}
              itemName={adjustStockLabel}
            />
          )}
        </>
      )}
    </div>
  );
};

export default UpdateItemInventoryLedger;
